import {
  ComponentPropsWithRef,
  ReactNode,
  Ref,
  createContext,
  useContext,
  useEffect,
  useId,
} from "react";
import styled, { css, keyframes } from "styled-components";
import { useDraggable } from "@dnd-kit/core";
import { DialogProps } from "@radix-ui/react-dialog";
import { zIndex } from "../../styles/constants";
import {
  elevation02,
  opacity00,
  shade800,
  shade950,
} from "../../styles/tokens";
import { Button } from "../button/Button.Parts";

// TEXT

// STYLES
const modalOpacityIn = keyframes`
  from {
    background-color: transparent;
  }
  to {
    background-color: ${opacity00};
  }
`;

const modalOpacityOut = keyframes`
  from {
    background-color: ${opacity00};
  }
  to {
    background-color: transparent;
  }
`;

export const Container = styled.div`
  display: flex;
  overflow: auto;
  flex-flow: column;
  flex-grow: 1;
  height: 100%; /* Prevent scrollbar flickering during the height animation */

  /* We add a padding-bottom and negative margin-bottom to create a "tail" */
  margin-bottom: -100rem;
  padding-bottom: 100rem;

  transition:
    box-shadow 0.5s ease-in-out,
    border-top 0.5s ease-in-out;
`;

export const Content = styled.div.attrs<ContentProps>(
  ({ $yOffset, $height, $dragProgress, $dragTargetHeight }) => {
    const interpolatedHeight =
      $dragTargetHeight && $dragProgress
        ? `calc(${$height} + (${$dragTargetHeight} - ${$height}) * ${Math.abs($dragProgress)})`
        : $height;

    return {
      style: {
        transform: `translateY(${$yOffset}px)`,
        height: interpolatedHeight
          ? `calc(${interpolatedHeight} + 5rem)`
          : undefined,
        // +5rem: the hidden tail (see padding-bottom below) isn't part of the visible cap
        maxHeight: "calc(95dvh + 5rem)",
      },
    };
  },
)<ContentProps>`
  background: ${shade950};
  position: fixed;
  display: flex;
  flex-direction: column;
  width: 100%;
  bottom: 0;
  z-index: ${({ $zIndex = zIndex.surface }) => $zIndex};
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  touch-action: none;
  will-change: transform, height;

  /* We add a padding-bottom and negative margin-bottom to create a "tail"
     that allows for rubber-banding when dragging up on the sheet */
  padding-bottom: 5rem;
  margin-bottom: -5rem;

  /* Ensure children don't get pushed into the padding */
  > *:not(${Container}) {
    flex-shrink: 0;
  }

  box-shadow: ${({ $showOverlay }) => ($showOverlay ? elevation02 : `none`)};

  transition: ${({ $isDragging, $open }) => {
    if ($isDragging) return "none";

    // Curve for closing
    if (!$open)
      return "transform 0.5s ease-in-out, height 0.5s ease-in-out, box-shadow 0.5s ease-in-out";

    // Curve for snapping and opening
    return "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), height 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s cubic-bezier(0.22, 1, 0.36, 1)";
  }};
`;

export const Overlay = styled.div<{
  $isClosing?: boolean;
  $isOpen: boolean;
  $showOverlay?: boolean;
}>`
  position: fixed;
  display: flex;
  z-index: ${zIndex.overlay};
  inset: 0;

  ${({ $isOpen, $isClosing, $showOverlay }) => {
    const fadeIn = $isOpen && !$isClosing && $showOverlay;
    const fadeOut = $isClosing || !$showOverlay;

    return css`
      pointer-events: ${fadeIn ? "auto" : "none"};
      animation: ${fadeOut ? modalOpacityOut : modalOpacityIn} 0.5s
        cubic-bezier(0.22, 1, 0.36, 1) forwards;
      ${!fadeIn && !fadeOut && "background-color: transparent;"}
    `;
  }}
`;

export const BottomSheetWrapper = styled.div<{
  $isOpen: boolean;
  $zIndex?: number;
}>`
  ${({ $isOpen, $zIndex = zIndex.surface }) =>
    $isOpen &&
    css`
      position: fixed;
      inset: 0;
      z-index: ${$zIndex}; // must match Content's $zIndex
      pointer-events: none;

      & > * {
        pointer-events: auto;
      }
    `}
`;

export const BottomSheetHeadWrapper = styled.div`
  flex-shrink: 0;
  padding: 0.5rem 1.5rem 1rem;

  border-bottom: 1px solid ${shade800};
`;

export const BottomSheetListWrapper = styled.div`
  flex-shrink: 1;
  overflow: auto;
  padding: 1rem 0 2rem;
`;

export const BottomSheetContentWrapper = styled.div`
  padding: 1.5rem 1.5rem 2rem;
`;

export const BottomSheetButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  padding: 1rem 1.5rem;
  justify-content: space-between;

  border-top: 1px solid ${shade800};

  /* the sheet is narrow — buy back room so footer button text never wraps */
  ${Button} {
    padding: 0 1rem;
  }
`;

// DATA

// TYPES
export interface Props
  extends Omit<ComponentPropsWithRef<"div">, "title" | "children">,
    Pick<DialogProps, "defaultOpen" | "open" | "onOpenChange"> {
  /**
   * Optional fixed height for the bottom sheet. If not provided, height will be based on content.
   */
  height?: string;
  /**
   * Whether the bottom sheet can be dragged open/closed
   * @default true
   */
  draggable?: boolean;
  /**
   * The drag distance threshold (as percentage of screen height) required to close the sheet
   * @default 15 (equivalent to 15% of screen height)
   */
  dragThreshold?: number;
  /**
   * A hidden title to be read by screen readers.
   *
   * Alternatively, you can provide a visible title as a child of the sheet. If so, please wrap it
   * inside a <ModalLabel /> component that receives the same id as the titleId prop
   */
  hiddenTitle?: string;
  /**
   * Used whenever a <ModalLabel /> child component wraps a text element inside the sheet that is used as the visible title instead of the hiddenTitle prop.
   * Please use the same id for the title element and this titleId prop to ensure proper accessibility linking between the title and the sheet.
   *
   * If this prop is provided, it gets priority over the hiddenTitle prop for accessibility purposes,
   * meaning that screen readers will link to the visible title element instead of the hidden title.
   */
  titleId?: string;
  /**
   * A hidden description to be read by screen readers. This can provide additional context about
   * the sheet's content or purpose. It will be linked to the sheet for accessibility when provided.
   *
   * Alternatively, you can provide a visible description as a child of the sheet. If so, please wrap the child
   * inside a <ModalLabel /> component that receives the same id as the descriptionId prop.
   */
  hiddenDescription?: string;
  /**
   * Used whenever a <ModalLabel /> child component wraps a text element inside the sheet that is used as the visible description instead of the hiddenDescription prop.
   * Please use the same id for the description element and this descriptionId prop to ensure proper accessibility linking between the description and the sheet.
   *
   * If this prop is provided, it gets priority over the hiddenDescription prop for accessibility purposes,
   * meaning that screen readers will link to the visible description element instead of the hidden description.
   */
  descriptionId?: string;
  /**
   * After close callback, this function is called right after the close animation
   */
  afterClose?: () => void;
  /**
   * The bottom sheet's content, can be anything including a `<DialogDescription />`
   */
  children?: ReactNode;
  /**
   * Optional snap points for the bottom sheet. If provided, the sheet will snap to these heights
   * when dragged. Heights can be in any CSS unit (e.g., "300px", "40rem"). You can also specify
   * a default snap point by adding `isDefault: true` to one of the points. If no default is specified,
   * the first snap point will be used as the default.
   */
  snapPoints?: SnapPointConfig[];
  /**
   * Ref for imperative control of the bottom sheet. Use this to programmatically snap to the first
   * snap point from the parent component via `controlRef.current?.snapToFirst()`.
   */
  controlRef?: Ref<BottomSheetControl>;
  /**
   * Overrides the sheet's content z-index (see the `zIndex` token export).
   * Defaults to `zIndex.surface`. Only needed when a specific sheet must
   * always stack above/below other sheets regardless of open/mount order —
   * e.g. the Music Bar sheet uses `zIndex.musicBarSurface`, the one tier
   * below, so any other sheet reliably covers it.
   */
  zIndex?: number;
}

export type SnapPointConfig = {
  /**
   * A unique key for the snap point. This is used to match the snap point to the corresponding
   * child content. Each child content must have a matching key as well.
   */
  key: string;
  /**
   * The height of the snap point. Can be in any CSS unit (e.g., "300px", "40rem").
   * If using snap points, it's important to ensure that the content for each snap point
   * can fit within its specified height to avoid overflow issues.
   */
  height: string; // e.g., "300px" or "40rem"
  /**
   * Whether this snap point is the default one that the sheet will open to. Only one snap point
   * can be the default. If no snap point is marked as default, the first one will be used as the default.
   */
  isDefault?: boolean;
  /**
   * Whether this snap point is the "home" position that the sheet will return to when the overlay is
   * clicked or the escape key is pressed. If not specified, the sheet will close instead of returning
   * to a home position. Only one snap point can be the home position. If multiple snap points are marked
   * as home, the first one will be used as the home position.
   */
  home?: boolean;
  /**
   * Whether to show the overlay when the sheet is at this snap point. This allows you to create a "modal"
   * snap point with the overlay, and other "non-modal" snap points without the overlay.
   */
  showOverlay?: boolean;
  /**
   * Optional background color for the drag handle when the sheet is at this snap point and when BottomSheet's draggable prop is true.
   */
  dragHandleBackgroundColor?: string;
};

export type BottomSheetControl = {
  /**
   * Snaps the bottom sheet back to the first (smallest) snap point. Useful for resetting the sheet
   * to its initial position from the parent component.
   */
  snapToFirst: () => void;
};

type ContentProps = {
  $yOffset: number;
  $dragProgress: number;
  $dragTargetHeight?: string;
  $isDragging: boolean;
  $isOpening: boolean;
  $open?: boolean;
  $height?: string;
  $showOverlay?: boolean;
  $zIndex?: number;
};

type ToggleContextValue = { toggleSnapPoint: () => void };

type SnapGeometry = {
  floorResistancePx: number;
  nextSnapHeight?: string;
  prevSnapHeight?: string;
  dragTargetHeight?: string;
};

// CONSTANTS
export const RUBBER_BAND_SENSITIVITY = 300; // How easily it pulls up
export const MAX_RUBBER_BAND_DISTANCE = 80; // Max distance (should match tail size)
export const RUBBER_BAND_RESISTANCE = 0.5; // Dampens the atan curve's approach to MAX_RUBBER_BAND_DISTANCE

const ToggleContext = createContext<ToggleContextValue | null>(null);
export const BottomSheetToggleProvider = ToggleContext.Provider;

// FUNCTIONS
export const percentToPixels = (percent: number) =>
  (percent / 100) * window.innerHeight;

// Converts a rem/px CSS length string to pixels; null for anything else
export const parseLengthToPixels = (value?: string): number | null => {
  if (!value) return null;

  const remMatch = value.match(/^(-?[\d.]+)rem$/);
  if (remMatch) {
    const rootFontSizePx = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );
    return parseFloat(remMatch[1]) * rootFontSizePx;
  }

  const pxMatch = value.match(/^(-?[\d.]+)px$/);
  if (pxMatch) return parseFloat(pxMatch[1]);

  return null;
};

export const getSnapGeometry = (
  snapPoints: SnapPointConfig[] | undefined,
  activeSnapPoint: number,
  activeSnapHeight: string | undefined,
  dragProgress: number,
): SnapGeometry => {
  // "Hit the bottom" resistance when there's no smaller snap point to shrink into
  const floorResistancePx =
    (parseLengthToPixels(activeSnapHeight) ?? MAX_RUBBER_BAND_DISTANCE) * 0.2;

  const nextSnapHeight =
    snapPoints && activeSnapPoint < snapPoints.length - 1
      ? snapPoints[activeSnapPoint + 1].height
      : undefined;
  const prevSnapHeight =
    snapPoints && activeSnapPoint > 0
      ? snapPoints[activeSnapPoint - 1].height
      : activeSnapHeight
        ? `calc(${activeSnapHeight} - ${floorResistancePx}px)`
        : undefined;
  const dragTargetHeight =
    dragProgress > 0
      ? nextSnapHeight
      : dragProgress < 0
        ? prevSnapHeight
        : undefined;

  return {
    floorResistancePx,
    nextSnapHeight,
    prevSnapHeight,
    dragTargetHeight,
  };
};

export const useEscapeKey = (onEscape: () => void, isActive: boolean) => {
  useEffect(() => {
    if (!isActive) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onEscape();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onEscape, isActive]);
};

// Used by DragTapSurface to move toward/away from the sheet's home snap point on tap
export const useBottomSheetToggle = () => {
  const context = useContext(ToggleContext);

  if (!context)
    throw new Error("useBottomSheetToggle must be used within a BottomSheet");

  return context.toggleSnapPoint;
};

// Wires the sheet's drag-to-close/move-between-snap-points gesture functionality into an
// element — no visual handle, no tap behavior
export const useBottomSheetDragSurface = (id?: string) => {
  const generatedId = useId();
  // A stable, readable id (see DragTapSurface) makes multiple simultaneous
  // drag surfaces easier to tell apart in dnd-kit's active.id while
  // debugging; used for both the DOM id and dnd-kit's id so they stay in sync
  const resolvedId = id ?? generatedId;
  const { setNodeRef, listeners } = useDraggable({ id: resolvedId });

  return { id: resolvedId, ref: setNodeRef, ...listeners };
};

// ASSETS
